import type { ReactNode } from 'react';

interface CpmFilterChipProps {
    variant?: 'filter';
    active?: boolean;
    onClick?: () => void;
    disabled?: boolean;
    children: ReactNode;
    className?: string;
}

interface CpmTokenChipProps {
    variant: 'token';
    onRemove: () => void;
    children: ReactNode;
    className?: string;
}

interface CpmTagChipProps {
    variant: 'tag';
    children: ReactNode;
    className?: string;
}

type CpmChipProps = CpmFilterChipProps | CpmTokenChipProps | CpmTagChipProps;

function CloseIcon() {
    return (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

/**
 * Matches the design system's `Chip` component exactly (see docs2
 * `_ds_bundle.js`) — `filter` variant is the pill trigger used for
 * States/Tags/Licence type/Active/Inactive; `token` variant is the
 * removable pill used in the applied-filters strip.
 */
export default function CpmChip(props: CpmChipProps) {
    if (props.variant === 'tag') {
        return (
            <span
                className={['inline-flex items-center', props.className ?? ''].join(' ')}
                style={{
                    padding: '3px 8px',
                    borderRadius: 'var(--cpm-radius-6)',
                    background: 'var(--cpm-surface-tag)',
                    color: 'var(--cpm-text-primary)',
                    fontFamily: 'var(--cpm-font-sans)',
                    fontSize: 11.5,
                    fontWeight: 700,
                }}
            >
                {props.children}
            </span>
        );
    }
    if (props.variant === 'token') {
        return (
            <span
                className={['inline-flex items-center', props.className ?? ''].join(' ')}
                style={{
                    gap: 6,
                    padding: '4px 6px 4px 10px',
                    borderRadius: 'var(--cpm-radius-full)',
                    background: 'var(--cpm-purple-selected)',
                    border: '1.5px solid var(--cpm-brand-purple)',
                    color: 'var(--cpm-brand-navy)',
                    fontFamily: 'var(--cpm-font-sans)',
                    fontSize: 11.5,
                    fontWeight: 700,
                }}
            >
                {props.children}
                <button
                    type="button"
                    onClick={props.onRemove}
                    aria-label="Remove"
                    className="inline-flex items-center justify-center border-0 cursor-pointer"
                    style={{
                        width: 16,
                        height: 16,
                        padding: 0,
                        borderRadius: 'var(--cpm-radius-full)',
                        background: 'rgba(14,8,88,0.08)',
                        color: 'var(--cpm-brand-navy)',
                    }}
                >
                    <CloseIcon />
                </button>
            </span>
        );
    }

    const { active = false, onClick, disabled = false, children, className = '' } = props;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                'inline-flex items-center transition-colors duration-150',
                active
                    ? ''
                    : 'hover:border-[var(--cpm-brand-purple)] hover:bg-[var(--cpm-page-bg)]',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                className,
            ].join(' ')}
            style={{
                height: 30,
                padding: '0 13px',
                borderRadius: 'var(--cpm-radius-full)',
                border: `1.5px solid ${active ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                background: active ? 'var(--cpm-purple-selected)' : '#fff',
                color: active ? 'var(--cpm-brand-navy)' : 'var(--cpm-text-primary)',
                fontFamily: 'var(--cpm-font-sans)',
                fontSize: 12.5,
                fontWeight: 800,
            }}
        >
            {children}
        </button>
    );
}
